import { useWorkspace } from '@/contexts/WorkspaceContext'
import { MODULE_LABEL_KEYS } from '@/lib/moduleLabels'
import { Link, useRouterState } from '@tanstack/react-router'
import { ChevronRight, Home } from 'lucide-react'
import { Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

type Crumb = { label: string; to?: string }

const OPAQUE_ID = /^([0-9a-f]{8}-[0-9a-f]{4}|\d+|[A-Z0-9]{8,})/i

function humanize(segment: string): string {
  return segment.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Workspace → module → trailing-segment breadcrumb trail.
 * Hidden on top-level module pages (the page title is enough there).
 */
export function Breadcrumbs() {
  const { t } = useTranslation(['common', 'workspaces'])
  const { current } = useWorkspace()
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  const crumbs = useMemo<Crumb[]>(() => {
    if (!current) return []

    // Longest matching module path for the current location.
    const module = current.primary_modules
      .filter((m) => m.path && (pathname === m.path || pathname.startsWith(`${m.path}/`)))
      .sort((a, b) => b.path.length - a.path.length)[0]

    const base = module?.path ?? current.home_path
    if (!base || pathname === base) return [] // top-level page → no trail

    const out: Crumb[] = []
    const wsName = t(`workspaces:workspaces.${current.code}.name`, current.name)
    out.push({ label: wsName, to: current.home_path })
    if (module && module.path !== current.home_path) {
      const key = MODULE_LABEL_KEYS[module.code]
      out.push({ label: key ? t(key, module.label) : module.label, to: module.path })
    }

    // Remaining segments after the module/home base.
    const rest = pathname.slice(base.length).split('/').filter(Boolean)
    let acc = base
    rest.forEach((seg, i) => {
      acc += `/${seg}`
      const label = OPAQUE_ID.test(seg) ? t('common:breadcrumb.detail', 'Détail') : humanize(seg)
      out.push({ label, to: i < rest.length - 1 ? acc : undefined })
    })
    return out
  }, [current, pathname, t])

  if (crumbs.length === 0) return null

  return (
    <nav
      aria-label={t('common:breadcrumb.aria', "Fil d'Ariane")}
      className="mb-4 flex items-center gap-1 text-xs text-muted-foreground"
    >
      <Link
        to={current?.home_path ?? ('/dashboard' as never)}
        className="flex items-center rounded p-1 transition-colors hover:text-foreground"
        aria-label={t('common:breadcrumb.home', 'Accueil')}
      >
        <Home className="h-3.5 w-3.5" />
      </Link>
      {crumbs.map((c, i) => (
        <Fragment key={`${c.label}-${i}`}>
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
          {c.to ? (
            <Link
              to={c.to as never}
              className="truncate rounded px-1 py-0.5 transition-colors hover:text-foreground"
            >
              {c.label}
            </Link>
          ) : (
            <span className="truncate px-1 py-0.5 font-medium text-foreground" aria-current="page">
              {c.label}
            </span>
          )}
        </Fragment>
      ))}
    </nav>
  )
}
